import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type Sub = {
  plan_id: string;
  status: string;
  current_period_end: string | null;
  trial_end: string | null;
  plans: { kind: string; metadata: Record<string, unknown> | null } | null;
};

export type Entitlements = {
  loading: boolean;
  isAuthed: boolean;
  hasFounderPass: boolean;
  hasCoaching: boolean;
  isInTrial: boolean;
  /**
   * Gated AI mentors (KORA + Velyra) — unlocked for Founder Pass or Coaching
   * subscribers. Used by the in-app Mentoring hub and route guard.
   */
  canUseAiMentors: boolean;
  hasBootcamp: (slugOrId?: string | null) => boolean;
  bootcampIds: Set<string>;
};

export function useEntitlements(): Entitlements {
  const { data, isLoading } = useQuery({
    queryKey: ["entitlements"],
    staleTime: 60_000,
    queryFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      const uid = u?.user?.id;
      if (!uid) return { isAuthed: false, subs: [] as Sub[], bootcampIds: [] as string[] };

      const [{ data: subs }, { data: enrolls }] = await Promise.all([
        supabase
          .from("subscriptions")
          .select("plan_id, status, current_period_end, trial_end, plans:plans!inner(kind, metadata)")
          .eq("user_id", uid)
          .in("status", ["active", "trialing"]),
        supabase
          .from("bootcamp_enrollments")
          .select("bootcamp_id, status")
          .eq("user_id", uid)
          .in("status", ["active", "completed"]),
      ]);

      const now = Date.now();
      const valid = ((subs ?? []) as unknown as Sub[]).filter((s) =>
        !s.current_period_end || new Date(s.current_period_end).getTime() > now,
      );
      const bootcampIds = (enrolls ?? []).map((e: { bootcamp_id: string }) => e.bootcamp_id);
      return { isAuthed: true, subs: valid, bootcampIds };
    },
  });

  const subs = (data?.subs ?? []) as Sub[];
  const bootcampSet = new Set(data?.bootcampIds ?? []);
  const hasFounderPass = subs.some((s) => s.plans?.kind === "founder_pass");
  const hasCoaching = subs.some((s) => s.plans?.kind === "coaching");
  const isInTrial = subs.some((s) => s.trial_end && new Date(s.trial_end).getTime() > Date.now());

  return {
    loading: isLoading,
    isAuthed: !!data?.isAuthed,
    hasFounderPass,
    hasCoaching,
    isInTrial,
    canUseAiMentors: hasFounderPass || hasCoaching,
    bootcampIds: bootcampSet,
    hasBootcamp: (slugOrId) => (slugOrId ? bootcampSet.has(slugOrId) : false),
  };
}